import React, { useState, useEffect, useRef, useMemo } from 'react';
// @ts-ignore
import ReactDOM from 'react-dom';
// @ts-ignore
import Icon from 'tbk-components/src/components/Icon';
import cn from 'classnames';
import AnimatedArcs from '../AnimatedArcs';
import Button from 'tbk-components/src/components/Button';
import { cloneDeep, kebabCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import qs from 'qs';

const ResourcesFilterBar = (props: any) => {
	const [state, updateState] = useState<any>({ open: {} });
	const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
	const { onChange = (a: any) => {}, assets = {}, targetUrl = null } = props;
	const { t } = useTranslation();
	const ref = useRef(null);
	const toggleFilter = (key: any) => {
		updateState((s: any) => ({
			...s,
			open: {
				...s.open,
				[key]: !s.open[key],
			},
		}));
	};

	// Initial search value, even after it updates, this value should not change
	const initialSearchValue = props.filters?.search || '';
	const listStyle = props.listStyle || 'default';
	const showFilterBar = props.showFilterBar || true;
	const backgroundStyle = props.filterHeaderBackgroundStyle || 'light';
	const [advancedFiltersCollapsed, setAdvancedFiltersCollapsed] =
		useState<boolean>(listStyle === 'horizontal');
	const taxonomies = assets?.taxonomies || [];
	const filters = { ...(props.filters || {}) };

	const filterKeysToExclude = ['search', 'page', 'total'];
	const resultsLoaded = props.resultsLoaded || null;
	//If the resultsLoaded is set to true and advanced filters is open, collapse it
	// TM - I think this was a request because when there are a lot of filters, the results were
	// below the fold, but now that we've got fewer taxonomies, I'm going to disable this because
	// it's a bit jarring to have the filters collapse when the results load.
	// useEffect(() => {
	// 	if (!resultsLoaded && !advancedFiltersCollapsed) {
	// 		setAdvancedFiltersCollapsed(true);
	// 	}
	// }, [resultsLoaded]);

	let activeCount = 0;
	//Look through filters for any active filters
	for (const key in filters) {
		if (!filterKeysToExclude.includes(key)) {
			activeCount += filters[key].length;
		}
	}
	const clearedState: any = {
		search: filters.search || '',
		page: 1,
	};
	const goToAdvancedSearch = props.goToAdvancedSearch || false;
	const searchAction = props.searchAction || '';
	const commonTerms = props.commonTerms || {};

	taxonomies.forEach((taxonomy: any) => {
		if (!filters[taxonomy.taxonomy]) {
			filters[taxonomy.taxonomy] = [];
		}
		activeCount += filters[taxonomy.taxonomy].length;
		clearedState[taxonomy.taxonomy] = [];
	});
	// console.log('taxonomies', taxonomies);

	const generatedTaxonomies = useMemo(() => {
		const taxonomiesClone = cloneDeep(taxonomies);
		taxonomiesClone.forEach((taxonomy: any) => {
			if (!filters[taxonomy.taxonomy]) {
				filters[taxonomy.taxonomy] = [];
			}

			//Reset the taxonomies for a fresh search
			taxonomy?.terms &&
				taxonomy.terms.forEach((term: any) => {
					term.disabled = false;

					if (Object.keys(commonTerms).length > 0) {
						//If commonTerms, we want to whitelist the terms provided.
						//Disable the term by default.
						term.disabled = true;
						for (const key in commonTerms) {
							if (
								commonTerms[key].term === term.term_id &&
								commonTerms[key].taxonomy === taxonomy.taxonomy
							) {
								term.disabled = false;
							}
						}
					}
				});
			//TODO RESET ALL OPTIONS OR RANGES TOO

			activeCount += filters[taxonomy.taxonomy].length;
			clearedState[taxonomy.taxonomy] = [];
		});
		return taxonomiesClone;
	}, [taxonomies, commonTerms]);

	const genUniqueId = Math.random().toString(36).substr(2, 9);
	// For horizontal dropdown advanced filters toggle height
	// When a filter is clicked on, update the filters object and call the onChange function.
	const onCheckFilter = (e: any) => {
		e.preventDefault();
		const input = e?.target;
		const taxonomy = input.name;
		const isAll = input.value.match(/(^|:)all$/);

		if (isAll && input.checked) {
			filters[taxonomy] = [];
		} else {
			const index = filters[taxonomy].indexOf(input.value);
			if (input.checked) {
				if (index === -1) {
					filters[taxonomy].push(input.value);
				}
			} else if (index !== -1) {
				filters[taxonomy].splice(index, 1);
			}
		}
		onChange({
			[taxonomy]: filters[taxonomy],
			page: 1,
		});
	};

	const onSelectFilter = (e: any) => {
		const select = e.target;
		const value = select.value;
		const taxonomyId = select.getAttribute('taxonomy-id');

		filters[taxonomyId] = [];
		filters[taxonomyId].push(value);
		onChange({
			[taxonomyId]: filters[taxonomyId],
			page: 1,
		});
	};

	const onRangeFilter = (e: any, term: any, taxonomy: any) => {
		const select = e.target;
		const value = select.value;
		const termId = term.term_id;
		const taxonomyId = taxonomy.taxonomy;
		const generatedValue = `${termId}__${value}`;
		const index = filters[taxonomyId]?.findIndex((v: any) =>
			v.includes(termId),
		);

		if (select.value !== '') {
			if (index !== -1) {
				filters[taxonomyId].splice(index, 1);
			}
			filters[taxonomyId].push(generatedValue);
		} else {
			if (index !== -1) {
				filters[taxonomyId].splice(index, 1);
			}
		}

		onChange({
			[taxonomyId]: filters[taxonomyId],
			page: 1,
		});
	};

	const arcOne =
		props.filterHeaderBackgroundStyle === 'light'
			? 'var(--bs-light)'
			: props.filterHeaderBackgroundStyle === 'themed'
			? 'var(--bs-secondary)'
			: 'var(--bs-light)';

	const arcTwo =
		props.filterHeaderBackgroundStyle === 'light'
			? ''
			: props.filterHeaderBackgroundStyle === 'themed'
			? 'var(--bs-secondary)'
			: 'var(--bs-white)';

	useEffect(() => {
		// On horizontal layout, the advanced toggle will be a drop down but will always show the first menu item in the list.
		// So we must calculate the height of the first drop down item and set the max height of that collapseable menu to that height.

		if (generatedTaxonomies.length <= 1) {
			return;
		}
		if (ref.current) {
			const firstDropdown = ref.current.querySelector(
				'.filter-options .filter-item:first-child',
			);
			const filterOptions = ref.current.querySelector('.filter-options');
			// If window width is 768 px, turn off the resize listener and set max-height to be 0
			const setFirstDropdownSize = () => {
				const windowWidth = window.innerWidth;
				if (advancedFiltersCollapsed && filterOptions) {
					windowWidth < 769
						? (filterOptions.style.maxHeight = '0px')
						: (filterOptions.style.maxHeight = `${
								firstDropdown.getBoundingClientRect().height + 1
						  }px`);
				}
			};

			if (advancedFiltersCollapsed) {
				setFirstDropdownSize();
				window.addEventListener('resize', setFirstDropdownSize);
			} else {
				filterOptions.style.maxHeight = '3000px';
			}
			return () => {
				window.removeEventListener('resize', setFirstDropdownSize);
			};
		}
	}, [advancedFiltersCollapsed]);
	const filterSummaryRender =
		activeCount > 0 ? (
			<div
				className={cn(
					'filter-summary mw-1240px',
					listStyle === 'vertical' && 'px-3 mx-auto',
				)}
			>
				<ul className="filter-tags keywords mb-0 p-0 d-inline-flex flex-wrap align-items-center gap-2">
					{activeCount > 0 && (
						<li key="0" className="reset-filters list-unstyled">
							<button
								className="d-flex text-body-sm align-items-center gap-2 p-0 border-0 bg-transparent"
								type="button"
								onClick={() => onChange(clearedState)}
							>
								<Icon name="close" />
								{props.i18n?.resetFilters ||
									t('Reset All Filters')}
							</button>
						</li>
					)}
				</ul>
			</div>
		) : (
			''
		);
	const el = document.getElementById('ResourcesFilterBar-filters');
	const content = (
		<div
			className={cn(
				'resource-filter-bar',
				`resource-filter-bar--list-style-${listStyle}`,
			)}
			ref={ref}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (goToAdvancedSearch && searchAction) {
						const form = e.target as HTMLFormElement;
						const args = {} as any;
						const search = form.querySelector(
							'input[name="search"]',
						) as HTMLInputElement;
						if (search && search.value) {
							args.search = search.value;
						}
						if (props.searchTaxonomies?.taxonomies) {
							props.searchTaxonomies?.taxonomies.forEach(
								(taxonoy) => {
									// @ts-ignore
									const inputs = [
										...form.querySelectorAll(
											`input[name="${taxonoy.taxonomy}"]:checked`,
										),
									].filter(
										// @ts-ignore
										(input: HTMLInputElement) =>
											input.value !== 'all',
									);
									if (inputs.length) {
										args[taxonoy.taxonomy] = [];
										// @ts-ignore
										inputs.forEach(
											// @ts-ignore
											(input: HTMLInputElement) => {
												args[taxonoy.taxonomy].push(
													input.value,
												);
											},
										);
										args[taxonoy.taxonomy] =
											args[taxonoy.taxonomy].join(',');
									}
								},
							);
						}
						window.location.href =
							searchAction + `#!${qs.stringify(args)}`;

						props.onCancel();
					}
				}}
				className="filter-container mb-0"
				action={searchAction}
			>
				<div className="filter-content">
					<div
						className={cn(
							'filter-header',
							'position-relative',
							listStyle !== 'default' && 'p-3',
							listStyle === 'vertical' && 'bg-light',
							listStyle === 'horizontal' &&
								`filter-bg-style-${backgroundStyle}`,
						)}
					>
						<div
							className={cn(
								'filter-header-content',
								listStyle === 'horizontal' &&
									'py-1 pt-md-8 pb-md-6 mx-auto mw-1240px',
								'd-flex flex-column flex-md-row gap-3',
								'justify-content-between align-items-center',
								listStyle === 'horizontal' &&
									'pt-sm-10 pt-md-12 pt-xl-15 mt-xxl-5', //This adds up to 170px on xxl and is enough for the sticky header
							)}
						>
							{(props.filterHeader || props.filterExcerpt) && (
								<div
									className={cn(
										'filter-header-copy d-flex gap-2 w-100 flex-shrink-1 flex-grow-1 mw-lg-850px',
									)}
								>
									<div className="header-titles">
										{props.filterHeaderEyebrow?.label &&
										props.filterHeaderEyebrow?.href ? (
											<h4 className={cn('mb-2 d-block')}>
												<Button
													className={cn(
														'text-decoration-none',
														backgroundStyle ===
															'themed'
															? 'text-white'
															: 'text-primary',
													)}
													{...props.filterHeaderEyebrow}
												/>
											</h4>
										) : (
											<h4
												className={cn(
													'mb-2 d-block',
													backgroundStyle === 'themed'
														? 'text-white'
														: 'text-primary',
												)}
											>
												{
													props?.filterHeaderEyebrow
														?.label
												}
											</h4>
										)}
										{props.filterHeader && (
											<h1
												className={cn(
													'mb-0',
													backgroundStyle ===
														'themed' &&
														'text-white',
												)}
											>
												{props.filterHeader}
											</h1>
										)}
									</div>
									{props.filterExcerpt && (
										<p
											className={cn(
												'mb-0',
												backgroundStyle === 'themed' &&
													'text-white',
											)}
											dangerouslySetInnerHTML={{
												__html: props.filterExcerpt,
											}}
										/>
									)}
								</div>
							)}
							<div
								className={cn(
									'filter-item search-wrapper border-0 w-100 d-flex flex-column justify-content-md-between gap-2 gap-md-4 full-width',
									props.filterHeader || props.filterExcerpt
										? 'mw-lg-392px flex-md-column align-items-md-left'
										: ' flex-md-row align-items-md-center',

									props.filterHeaderEyebrow &&
										listStyle === 'horizontal' &&
										'align-self-lg-end',
								)}
							>
								<div className={cn('search w-100')}>
									<label
										htmlFor={cn(`filter-search-${genUniqueId}`)}
										className="visually-hidden"
									>
										{props.i18n?.filterSearch ||
											t('Calian Search')}
									</label>
									<input
										name="search"
										type="text"
										id={cn(`filter-search-${genUniqueId}`)}
										className="search__input"
										placeholder={
											props.searchPlaceholder ||
											t('Search')
										}
										value={props.value}
										aria-label={
											props.i18n?.search || t('Search')
										}
										onChange={
											props.onSearchChange
												? props.onSearchChange
												: (e) =>
														onChange({
															search: e.target
																.value,
															page: 1,
														})
										}
									/>
									<button
										type="submit"
										aria-label={
											props.i18n?.submitSearch ||
											t('Submit Search')
										}
										className="search__btn"
									>
										<Icon name="magnifying-glass" />
									</button>
								</div>
								{props.showSearchResultsCount && (
									<p className="text-body-sm ps-3 ps-md-0 mb-0 d-none d-md-block">
										{`${
											props.i18n?.resultsFor ||
											t('Results for')
										} '${initialSearchValue || ' '}' `}
										{props.value &&
											`(${
												props.total ? props.total : '0'
											} ${
												props.i18n?.results ||
												t('results')
											})`}
									</p>
								)}
							</div>
						</div>
						{listStyle === 'horizontal' && (
							<div className="filter-header-backdrop">
								<div className="d-none d-md-block">
									<AnimatedArcs
										arcsStyle="Component - Two Lines"
										arcsPosition="Right"
										arcOneColor={arcOne}
										arcTwoColor={arcTwo}
										arcOneStrokeWidth={4}
										arcTwoStrokeWidth={4}
										animationDelay={100}
										animationOffset={100}
									/>
								</div>
							</div>
						)}
					</div>
					{props.showFilterBar && (
						<>
							{props?.inProductIndex && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										setShowMobileFilters((s) => !s);
									}}
									aria-expanded={showMobileFilters}
									aria-controls="all-filters"
									data-bs-toggle="dropdown"
									type="button"
									className="d-lg-none all-filters-toggle dropdown-toggle"
								>
									{props?.i18n?.toggleFilters ||
										'Toggle Filters'}
								</button>
							)}
							<div
								id="all-filters"
								className={cn(
									'filter-options-container',
									listStyle === 'horizontal' &&
										'mx-auto px-3 px-md-4 bg-white',
									props?.inProductIndex &&
										`d-lg-block dropdown-menu ${
											showMobileFilters
												? ' toggled d-block'
												: ''
										}`,
								)}
							>
								<div className="filter-options-content d-flex flex-column flex-md-row-reverse gap-2 gap-md-4 justify-content-between w-100 mx-auto mw-1240px">
									{listStyle === 'horizontal' && (
										<div className="flex-shrink-0 filter-options-horizontal-sidebar mt-2 mt-md-4 d-flex justify-content-between justify-content-md-start flex-md-column flex-row gap-3 gap-md-1">
											<p className="mb-0 d-inline-block d-md-none">
												{props.showSearchResultsCount
													? `(${
															props.total
																? props.total
																: '0'
													  } ${
															props.i18n
																?.results ||
															t('results')
													  })`
													: null}
											</p>
											{generatedTaxonomies.length > 1 && (
												<div className="filter-options-advanced-filter-toggle">
													<button
														className="advanced-filter-toggle text-body-sm text-primary bg-transparent p-0 border-0 outline-0 d-flex gap-2 align-items-center btn-sm w-100"
														type="button"
														onClick={() => {
															setAdvancedFiltersCollapsed(
																(s) => !s,
															);
														}}
													>
														<i
															className={`advanced-filters-toggle--icon icon icon-${
																advancedFiltersCollapsed
																	? 'eye'
																	: 'eye-closed'
															}`}
														/>
														<span className="advanced-filters-toggle--label">
															{`${
																advancedFiltersCollapsed
																	? props.i18n
																			?.view ||
																	  t(
																			'View Filters',
																	  )
																	: props.i18n
																			?.hide ||
																	  t(
																			'Hide Filters',
																	  )
															}`}
														</span>
													</button>
												</div>
											)}
											<div className="d-none d-md-block">
												{filterSummaryRender}
											</div>
										</div>
									)}
									<div
										className={cn(
											'filter-options',
											listStyle === 'horizontal'
												? 'overflow-hidden'
												: 'w-100',
											'align-self-start justify-self-start w-100',
										)}
									>
										{props.searchTaxonomies &&
											generatedTaxonomies.map(
												(taxonomy: any, index) => {
													// rangeRender is used when a taxonomy needs to filter between two values such as `dB typ. min and max gain`.
													const rangeRender =
														taxonomy.ranges && (
															<div
																className="filter-range-inputs d-flex flex-column gap-3"
																data-taxonomy={
																	taxonomy.taxonomy
																}
															>
																{taxonomy.ranges.map(
																	(
																		range: any,
																		index,
																	) => (
																		<div
																			key={
																				index
																			}
																			className="filter-range-inputs--range-input"
																		>
																			<label
																				htmlFor={cn(
																					`${range.term_id}-${genUniqueId}`,
																				)}
																			>
																				{
																					range.label
																				}
																			</label>
																			<div className="d-flex filter-range-input-bar gap-2">
																				<input
																					type="number"
																					name={
																						range.slug ||
																						range.term_id
																					}
																					dataset-meta-for={
																						taxonomy.taxonomy
																					}
																					id={cn(
																						`${range.term_id}-${genUniqueId}`,
																					)}
																					step={
																						range.step
																					}
																					min={
																						0
																					}
																					value={
																						filters[
																							taxonomy
																								.taxonomy
																						]
																							?.find(
																								(
																									v: any,
																								) =>
																									v.includes(
																										range.term_id,
																									),
																							)
																							?.split(
																								'__',
																							)[1] ||
																						''
																					}
																					defaultValue={
																						range.default ||
																						0
																					}
																					onClick={(
																						e,
																					) => {
																						e.stopPropagation();
																					}}
																					onChange={(
																						e,
																					) => {
																						e.stopPropagation();
																						onRangeFilter(
																							e,
																							range,
																							taxonomy,
																							//Get all values from the range inputs in an array
																						);
																					}}
																				/>
																				{range.unit && (
																					<span className="range-unit">
																						{
																							range.unit
																						}
																					</span>
																				)}
																			</div>
																		</div>
																	),
																)}
															</div>
														);

													const checkboxInputRender =
														(
															term: any,
															subTerms: any = [],
															classNames: string[] = [],
														) => {
															const isAll =
																`${term.term_id}`.match(
																	/(^|:)all$/,
																);
															const theseChecked =
																filters[
																	taxonomy
																		.taxonomy
																] || [];
															/**
															 * If the term_id includes 'refine-frequency', we will display a button instead of a checkbox.
															 * Please refer the term defined in `GnssProducts.php get_taxonomies_and_terms()`
															 */
															const maybeSubTerms =
																typeof term.term_id ===
																	'string' &&
																term.term_id.includes(
																	'refine-frequency',
																);
															const handleChange =
																(e) => {
																	if (
																		isAll &&
																		!e
																			.target
																			.checked
																	) {
																		// This is them clicking on an already checked "All" input.
																		// That's a no-op.
																		e.stopPropagation();
																	} else {
																		props.onCheckFilter
																			? props.onCheckFilter(
																					e,
																			  )
																			: onCheckFilter(
																					e,
																			  );
																	}
																};
															const InputList =
																() => {
																	// If term.name has '>' in it, remove any text before it and the '>' itself.
																	// We want to store the term name without the '>' separately in order to not break the filter structure and maintain its original name in the object
																	const generatedTermName =
																		term.name.includes(
																			'>',
																		)
																			? term.name.split(
																					'>',
																			  )[1]
																			: term.name;
																	return (
																		<div
																			className={cn(
																				'form-check',
																				...classNames,
																				term.name.includes(
																					'>',
																				) &&
																					'indent',
																			)}
																		>
																			<input
																				id={
																					(term?.slug ||
																						term?.term_id) +
																					`-${genUniqueId}-${index}`
																				}
																				disabled={
																					term?.disabled
																				}
																				className="form-check-input"
																				type="checkbox"
																				value={
																					term?.slug ||
																					term?.term_id
																				}
																				// If checked, or if any sub term is checked, check the input.
																				checked={
																					isAll
																						? !theseChecked.length
																						: theseChecked.indexOf(
																								`${
																									term?.slug ||
																									term?.term_id
																								}`,
																						  ) !==
																						  -1
																				}
																				name={
																					taxonomy.taxonomy
																				}
																				onClick={(
																					e,
																				) => {
																					e.stopPropagation();
																				}}
																				onChange={
																					handleChange
																				}
																			/>
																			<label
																				className="form-check-label d-block"
																				htmlFor={
																					(term?.slug ||
																						term?.term_id) +
																					`-${genUniqueId}-${index}`
																				}
																				key={
																					term?.slug ||
																					term?.term_id
																				}
																				onClick={(
																					e,
																				) => {
																					if (
																						listStyle ===
																						'vertical'
																					) {
																						const input =
																							ref.current.querySelector(
																								`input[id="${term?.term_id}-${genUniqueId}-${index}"]`,
																							) as HTMLInputElement;
																						input.click();
																					}
																					e.stopPropagation();
																				}}
																			>
																				{
																					generatedTermName
																				}
																				{(term?.count ||
																					term?.count ===
																						0) &&
																					filterCountRender(
																						term?.count,
																					)}
																			</label>
																		</div>
																	);
																};
															const disableDropdownButton =
																filters[
																	taxonomy
																		.taxonomy
																][0] !==
																	'Dual Band' &&
																filters[
																	taxonomy
																		.taxonomy
																][0] !==
																	'Single Band';

															return (
																<>
																	{!maybeSubTerms ? (
																		<InputList />
																	) : (
																		<div
																			className={cn(
																				'filter dropdown filter-checkboxes',
																				state
																					.open[
																					kebabCase(
																						term.name,
																					)
																				]
																					? ' toggled'
																					: '',

																				'dropdown',
																			)}
																			key={kebabCase(
																				term.name,
																			)}
																		>
																			<button
																				type="button"
																				id={`dropdownMenu-filter-${kebabCase(
																					term.name,
																				)}-${genUniqueId}-${index}`}
																				className="filter-menu dropdown-toggle custom-dropdown-button"
																				data-bs-toggle="dropdown"
																				aria-expanded="false"
																				onChange={() => {
																					toggleFilter(
																						kebabCase(
																							term.name,
																						),
																					);
																				}}
																				onClick={(
																					e,
																				) => {
																					e.stopPropagation();
																				}}
																				disabled={
																					disableDropdownButton
																				}
																				style={{
																					opacity:
																						disableDropdownButton
																							? 0.5
																							: 1.0,
																				}}
																			>
																				<div className="taxonomy-label gap-1 d-flex align-items-center">
																					{
																						term.name
																					}
																				</div>
																			</button>
																			{subTerms?.length >
																				0 && (
																				<ul
																					className="dropdown-menu checkboxes"
																					aria-labelledby={`dropdownMenu-filter-${kebabCase(
																						term.name,
																					)}-${genUniqueId}-${index}`}
																				>
																					{subTerms.map(
																						(
																							s,
																							index,
																						) => (
																							<li
																								key={
																									index
																								}
																							>
																								<div className="form-check">
																									<input
																										id={`${s.term_id}-${genUniqueId}-${index}`}
																										className="form-check-input"
																										type="checkbox"
																										name={
																											taxonomy.taxonomy
																										}
																										value={
																											s.term_id
																										}
																										// If checked, or if any sub term is checked, check the input.
																										checked={
																											theseChecked.indexOf(
																												s.term_id,
																											) !==
																											-1
																										}
																										onClick={(
																											e,
																										) => {
																											e.stopPropagation();
																										}}
																										onChange={
																											handleChange
																										}
																										disabled={term?.unavailableFrequencies?.includes(
																											s.term_id,
																										)}
																									/>
																									<label
																										className="form-check-label"
																										htmlFor={`${s.term_id}-${genUniqueId}-${index}`}
																										onClick={(
																											e,
																										) => {
																											e.stopPropagation();
																										}}
																									>
																										{
																											s.name
																										}
																									</label>
																								</div>
																							</li>
																						),
																					)}
																				</ul>
																			)}
																		</div>
																	)}
																</>
															);
														};

													const taxonomyLabelRender =
														(
															<div className="taxonomy-label gap-1 d-flex align-items-center">
																{taxonomy.label}

																{taxonomy.description && (
																	<div className="filter-description position-relative d-none d-md-block">
																		<Icon name="question" />
																		<div className="filter-description-bubble bg-white rounded-4 p-3 position-absolute border border-1 border-light">
																			<p className="mb-0 w-100 d-block">
																				{
																					taxonomy.description
																				}
																			</p>
																		</div>
																	</div>
																)}
															</div>
														);

													// Render the count node used in various terms
													const filterCountRender = (
														count: number,
													) => (
														<b className="filter-count">
															({count})
														</b>
													);
													// Default listing render for checkboxes
													const listing = (
														<ul
															className="dropdown-menu checkboxes"
															aria-labelledby={`dropdownMenu-filter-${taxonomy.taxonomy}-${genUniqueId}-${index}`}
														>
															{taxonomy?.terms
																?.length > 0 &&
																taxonomy?.terms.map(
																	(
																		term: any,
																	) => (
																		<li
																			key={
																				term?.slug ||
																				term?.term_id
																			}
																			data-term-id={
																				term?.slug ||
																				term?.term_id
																			}
																		>
																			{/* If an href is provided, the term will be a link and not a filter checkbox input. */}
																			{term?.href ? (
																				<a href="#">
																					{
																						term?.name
																					}
																					{(term?.count ||
																						term?.count ===
																							0) &&
																						filterCountRender(
																							term?.count,
																						)}
																				</a>
																			) : (
																				<div
																					className={cn(
																						'filter-input--form-check',
																						term?.ranges &&
																							'filter-input--ranges',
																						term?.options &&
																							'filter-input--form-check-with-options',
																					)}
																				>
																					{term?.options &&
																						!term?.disabled && (
																							<div className="filter-input-select">
																								<select
																									aria-label={
																										props
																											.i18n
																											?.selectOptionText ||
																										'Select a filter option from the dropdown'
																									}
																									className="filter-input-select--input"
																									taxonomy-id={
																										taxonomy.taxonomy
																									}
																									term-id={
																										term.term_id
																									}
																									value={
																										filters[
																											taxonomy
																												.taxonomy
																										][0]
																									}
																									onChange={(
																										e,
																									) => {
																										// Update the selected value in the filters object and call the onChange function.
																										onSelectFilter(
																											e,
																										);

																										// Find the option that matches the selected value. See if isTermOptionAvailable is false.
																										// If it is false, disable the term?.
																										const option =
																											term?.options.find(
																												(
																													o: any,
																												) =>
																													o.term_id ===
																													e
																														.target
																														.value,
																											);

																										// Push this term to the filters object
																										filters[
																											taxonomy.taxonomy
																										] =
																											[];
																										filters[
																											taxonomy
																												.taxonomy
																										].push(
																											option.term_id,
																										);

																										const btn =
																											ref.current?.querySelector(
																												`li[data-term-id="${term?.term_id}"] button`,
																											) as HTMLButtonElement;
																										const el =
																											ref.current?.querySelectorAll(
																												`li[data-term-id="${term?.term_id}"] input`,
																											) as NodeListOf<HTMLInputElement>;

																										for (
																											let i = 0;
																											i <
																											el.length;
																											i++
																										) {
																											if (
																												!option.isTermOptionAvailable
																											) {
																												el[
																													i
																												].disabled =
																													true;

																												// If input is checked previously but termOptionAvailable is false, uncheck the element.
																												if (
																													el[
																														i
																													]
																														.checked
																												) {
																													el[
																														i
																													].checked =
																														false;
																													onCheckFilter(
																														{
																															target: {
																																name: taxonomy.taxonomy,
																																checked:
																																	false,
																																value: term,
																															},
																														},
																													);
																												}
																											} else {
																												el[
																													i
																												].disabled =
																													false;

																												if (
																													option
																														?.unavailableFrequencies
																														?.length >
																														0 &&
																													option?.unavailableFrequencies.includes(
																														'all',
																													)
																												) {
																													el[
																														i
																													].disabled =
																														true;
																												}

																												if (
																													option
																														?.unavailableFrequencies
																														?.length >
																														0 &&
																													option?.unavailableFrequencies?.includes(
																														el[
																															i
																														]
																															.value,
																													)
																												) {
																													el[
																														i
																													].disabled =
																														true;
																												}
																											}
																										}
																									}}
																								>
																									{term?.options.map(
																										(
																											option: any,
																										) => (
																											<option
																												key={
																													option.term_id
																												}
																												value={
																													option.term_id
																												}
																												selected={
																													//If it is inside filters[meta_query], we  need to check if the value is the same as the selected value
																													filters[
																														taxonomy
																															.taxonomy
																													][0] ===
																													option.term_id
																												}
																											>
																												{
																													option.name
																												}
																											</option>
																										),
																									)}
																								</select>
																							</div>
																						)}
																					{checkboxInputRender(
																						term,
																						term.frequencies,
																					)}
																				</div>
																			)}

																			{term?.terms && (
																				<ul className="sub-checkboxes">
																					{term?.terms.map(
																						(
																							subTerm: any,
																						) => (
																							<li
																								key={
																									subTerm.term_id
																								}
																							>
																								{checkboxInputRender(
																									subTerm,
																									[
																										'filter-input--form-check',
																									],
																								)}
																							</li>
																						),
																					)}
																				</ul>
																			)}
																		</li>
																	),
																)}
															{rangeRender &&
																rangeRender}
														</ul>
													);
													return !taxonomy.hidden ? (
														<div
															key={index}
															className={`filter-item filter-item__${taxonomy.taxonomy}`}
														>
															<div
																className={cn(
																	'filter dropdown filter-checkboxes',
																	state.open[
																		taxonomy
																			.taxonomy
																	]
																		? ' toggled'
																		: '',

																	'dropdown',
																)}
																key={
																	taxonomy.taxonomy
																}
															>
																<button
																	onChange={() =>
																		toggleFilter(
																			taxonomy.taxonomy,
																		)
																	}
																	className="filter-menu dropdown-toggle"
																	type="button"
																	id={`dropdownMenu-filter-${taxonomy.taxonomy}-${genUniqueId}-${index}`}
																	data-bs-toggle="dropdown"
																	aria-expanded="false"
																>
																	{
																		taxonomyLabelRender
																	}
																	{filters[
																		taxonomy
																			.taxonomy
																	].length ? (
																		<div className="active">
																			<span className="visually-hidden">
																				{props
																					.i18n
																					?.activeFilter ||
																					'active filter'}
																			</span>
																		</div>
																	) : null}
																</button>
																{listing &&
																	listing}
															</div>
														</div>
													) : (
														<></>
													);
												},
											)}
										{filterSummaryRender && (
											<div className="d-block d-lg-none">
												{filterSummaryRender}
											</div>
										)}
									</div>
								</div>
							</div>
						</>
					)}
					{targetUrl && (
						<div className="filter-go-btn">
							<a
								className="btn btn-primary btn-sm"
								href={targetUrl}
							>
								{props.i18n?.go || t('Go')}
							</a>
						</div>
					)}
				</div>
				{listStyle === 'vertical' && (
					<div className="d-none d-lg-block">
						{filterSummaryRender}
					</div>
				)}
			</form>
		</div>
	);

	return el ? ReactDOM.createPortal(content, el) : content;
};

export default ResourcesFilterBar;
